import { Phone, MessageSquareText, Clock, MapPin, Star } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import QuoteForm from "@/components/QuoteForm";
import { Reveal, SectionHead } from "@/components/Reveal";
import { PHONE_DISPLAY, TEL_LINK, smsLink, waLink, DEFAULT_MSG, GOOGLE_REVIEW_URL } from "@/constants/site";

const CONTACT_CARDS = [
  {
    icon: Phone,
    title: "Call Dispatch",
    line: PHONE_DISPLAY,
    sub: "Fastest for emergencies",
    href: TEL_LINK,
    testid: "contact-call-card",
    cls: "hover:border-emergency",
  },
  {
    icon: MessageSquareText,
    title: "Text Us",
    line: PHONE_DISPLAY,
    sub: "Send your location by SMS",
    href: smsLink(DEFAULT_MSG),
    testid: "contact-sms-card",
    cls: "hover:border-emergency",
  },
  {
    icon: FaWhatsapp,
    title: "WhatsApp",
    line: PHONE_DISPLAY,
    sub: "Chat with dispatch instantly",
    href: waLink(DEFAULT_MSG),
    testid: "contact-whatsapp-card",
    cls: "hover:border-dispatch",
  },
];

const Contact = () => (
  <>
    <section className="bg-asphalt py-20 sm:py-28 grain relative" data-testid="contact-page-hero">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="font-mono text-xs tracking-[0.3em] text-emergency uppercase">Contact</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase leading-[1.02] text-white">
          Get a truck<br /><span className="text-emergency">rolling now.</span>
        </h1>
      </div>
    </section>

    <section className="bg-[#F8F9FA] py-20 sm:py-28" data-testid="contact-page-content">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 grid gap-12 lg:grid-cols-[1fr_1.1fr]">
        <div>
          <SectionHead
            eyebrow="Reach Us"
            title="Three ways to dispatch"
          />
          <div className="space-y-4">
            {CONTACT_CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.07}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={c.testid}
                  className={`group flex items-center gap-5 border border-zinc-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 ${c.cls}`}
                >
                  <c.icon className="h-8 w-8 text-emergency shrink-0" strokeWidth={1.8} />
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500">{c.title}</p>
                    <p className="font-display text-xl font-bold uppercase text-zinc-900">{c.line}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{c.sub}</p>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.25}>
            <div className="mt-8 space-y-4 border border-zinc-200 bg-white p-6">
              <p className="flex items-center gap-3 text-sm text-zinc-700">
                <Clock className="h-4 w-4 text-dispatch shrink-0" /> Open 24 hours, 7 days a week, 365 days a year
              </p>
              <p className="flex items-center gap-3 text-sm text-zinc-700">
                <MapPin className="h-4 w-4 text-dispatch shrink-0" /> Serving all of Tarrant County, TX
              </p>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-review-link"
                className="flex items-center gap-3 text-sm text-zinc-700 transition-colors hover:text-emergency"
              >
                <Star className="h-4 w-4 text-amber shrink-0" /> Leave us a Google review
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="bg-charcoal p-6 sm:p-10 grain relative" data-testid="contact-quote-form-card">
            <p className="font-mono text-xs tracking-[0.3em] text-emergency uppercase">Quote Request</p>
            <h2 className="mt-2 mb-7 font-display text-2xl sm:text-3xl font-black uppercase text-white">
              Tell us where you are
            </h2>
            <QuoteForm />
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default Contact;
